import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { vehiclesAPI, clientsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDateBritish } from '../utils/dateUtils';
import './VehicleList.css';

interface Vehicle {
  id: number;
  registration: string;
  make: string;
  model: string;
  vehicleType?: string;
  year?: string;
  vehicleValue?: number;
  coverStatus?: string;
  isActive: boolean;
  inceptionDate?: string;
  coverEndDate?: string;
  site?: { id: number; name: string };
  tempCoverInfo?: any;
}

type FleetTab = 'current' | 'removed' | 'blended';

const statusColour = (v: Vehicle): string => {
  if (v.isActive === false) return '#ef4444';
  if (v.coverStatus === 'Active')                                           return '#10b981';
  if (v.coverStatus === 'Cover Pending' || v.coverStatus === 'Pending Cover') return '#f59e0b';
  if (v.coverStatus === 'Inactive')                                         return '#6b7280';
  return '#374151';
};

const statusLabel = (v: Vehicle): string => {
  if (v.isActive === false)                                                return 'Removed';
  if (v.coverStatus === 'Active')                                          return 'On Cover';
  if (v.coverStatus === 'Cover Pending' || v.coverStatus === 'Pending Cover') return 'Pending Cover';
  if (v.coverStatus === 'Inactive')                                        return 'Off Cover';
  return v.coverStatus || '—';
};

const TABS: { key: FleetTab; icon: string; label: string; description: string }[] = [
  { key: 'current', icon: '✅', label: 'Live on Cover Fleet',   description: 'Vehicles currently on cover or awaiting approval' },
  { key: 'removed', icon: '⛔', label: 'Removed / Off Cover',   description: 'Vehicles that have been removed from the policy' },
  { key: 'blended', icon: '📋', label: 'Full Policy Vehicle Transactions List', description: 'All vehicles across all statuses in one report' },
];

const ClientInsights: React.FC = () => {
  const { user, isMTA } = useAuth();
  const [vehicles, setVehicles]       = useState<Vehicle[]>([]);
  const [loading, setLoading]         = useState(false);
  const [generated, setGenerated]     = useState(false);
  const [activeTab, setActiveTab]     = useState<FleetTab>('current');
  // MTA client selector
  const [clients, setClients]         = useState<{ id: number; companyName: string }[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);

  useEffect(() => {
    if (!isMTA) return;
    clientsAPI.getAll()
      .then(res => setClients(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, [isMTA]);

  const effectiveClientId = isMTA ? selectedClientId : (user?.clientId ?? null);

  const loadData = async () => {
    if (!effectiveClientId) return;
    setLoading(true);
    try {
      const res = await vehiclesAPI.getByClient(effectiveClientId);
      // Exclude bulk-upload-undone phantom vehicles: IsActive=false but CoverStatus !== 'Inactive'.
      // Genuine removals always have CoverStatus='Inactive'; phantoms were never approved.
      const realVehicles = (Array.isArray(res.data) ? res.data : []).filter(
        (v: any) => v.isActive || v.coverStatus === 'Inactive'
      );
      setVehicles(realVehicles);
      setGenerated(true);
    } catch {
      alert('Failed to load fleet data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Current fleet = non-deleted, non-inactive
  const currentFleet = vehicles.filter(
    (v) => v.isActive !== false && v.coverStatus !== 'Inactive'
  );
  // Removed = soft-deleted OR explicitly Inactive cover status
  const removedVehicles = vehicles.filter(
    (v) => v.isActive === false || v.coverStatus === 'Inactive'
  );
  // Blended = everything
  const blendedFleet = vehicles;

  const activeData: Vehicle[] =
    activeTab === 'current' ? currentFleet :
    activeTab === 'removed' ? removedVehicles :
    blendedFleet;

  // Excel sheet names must be ≤31 chars and must not contain: \ / ? * [ ] :
  const sanitiseSheetName = (name: string) =>
    name.replace(/[\\/\?\*\[\]\:]/g, '-').slice(0, 31);

  const exportToExcel = () => {
    const tabMeta = TABS.find((t) => t.key === activeTab)!;
    const rows = activeData.map((v) => ({
      'Registration':    v.registration,
      'Make / Model':    `${v.make} ${v.model}`,
      'Type':            v.vehicleType  || '—',
      'Year':            v.year         || '—',
      'Cover Status':    statusLabel(v),
      'Cover Type':      v.tempCoverInfo ? 'Temp' : 'Perm',
      'Inception Date':  v.inceptionDate ? formatDateBritish(v.inceptionDate) : '—',
      'Policy End Date': v.coverEndDate  ? formatDateBritish(v.coverEndDate)  : '—',
      'Site / Location': v.site?.name   || '—',
      'Value':           v.vehicleValue != null ? `£${v.vehicleValue.toLocaleString()}` : '—',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), sanitiseSheetName(tabMeta.label));
    XLSX.writeFile(wb, `Fleet_Report_${tabMeta.label.replace(/ \/ /g, '_').replace(/ /g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const renderTable = (rows: Vehicle[]) => (
    <div style={{ overflowX: 'auto' }}>
      <table>
        <thead>
          <tr>
            <th>Registration</th>
            <th>Make / Model</th>
            <th>Type</th>
            <th>Year</th>
            <th>Cover Status</th>
            <th>Cover Type</th>
            <th>Inception Date</th>
            <th>Policy End</th>
            <th>Site / Location</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={10} style={{ textAlign: 'center', color: '#9ca3af', fontStyle: 'italic', padding: '28px 0' }}>
                No vehicles in this category.
              </td>
            </tr>
          ) : rows.map((v) => (
            <tr key={v.id}>
              <td><strong>{v.registration}</strong></td>
              <td>{v.make} {v.model}</td>
              <td>{v.vehicleType || '—'}</td>
              <td>{v.year       || '—'}</td>
              <td>
                <span style={{ color: statusColour(v), fontWeight: 600 }}>
                  {statusLabel(v)}
                </span>
              </td>
              <td>{v.tempCoverInfo ? 'Temp' : 'Perm'}</td>
              <td>{v.inceptionDate ? formatDateBritish(v.inceptionDate) : '—'}</td>
              <td>{v.coverEndDate  ? formatDateBritish(v.coverEndDate)  : '—'}</td>
              <td>{v.site?.name   || '—'}</td>
              <td>{v.vehicleValue != null ? `£${v.vehicleValue.toLocaleString()}` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const countFor = (key: FleetTab) =>
    key === 'current' ? currentFleet.length :
    key === 'removed' ? removedVehicles.length :
    blendedFleet.length;

  return (
    <div className="vehicle-list">
      <div className="page-header">
        <h1>Insights &amp; Reports</h1>
      </div>

      {/* ── Generate panel ── */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h3 style={{ marginBottom: 8 }}>Fleet Reports</h3>
        <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 16 }}>
          Generate a snapshot of your fleet across all cover statuses — vehicles on cover, pending approval, and previously removed.
          Export any report to Excel for your own records or insurer submissions.
        </p>
        {isMTA && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 6 }}>Select Client *</label>
            <select
              value={selectedClientId ?? ''}
              onChange={e => { setSelectedClientId(Number(e.target.value) || null); setGenerated(false); setVehicles([]); }}
              style={{ padding: '8px 12px', border: '1.5px solid #d1d5db', borderRadius: 6, fontSize: 14, minWidth: 280, background: '#fff' }}
            >
              <option value="">— Select a client —</option>
              {clients.sort((a, b) => a.companyName.localeCompare(b.companyName)).map(c => (
                <option key={c.id} value={c.id}>{c.companyName}</option>
              ))}
            </select>
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={loadData} disabled={loading || !effectiveClientId}>
            {loading ? 'Loading…' : generated ? '↺ Refresh' : 'Generate Reports'}
          </button>
          {generated && (
            <span style={{ color: '#6b7280', fontSize: 13 }}>
              {vehicles.length} vehicle{vehicles.length !== 1 ? 's' : ''} total
              &nbsp;&mdash;&nbsp;
              <span style={{ color: '#10b981', fontWeight: 600 }}>{currentFleet.length} on cover / pending</span>
              &nbsp;&middot;&nbsp;
              <span style={{ color: '#6b7280' }}>{removedVehicles.length} removed / off cover</span>
            </span>
          )}
        </div>
      </div>

      {generated && (
        <>
          {/* ── Tab bar ── */}
          <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid #e5e7eb' }}>
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: '10px 24px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 14,
                  color: activeTab === tab.key ? '#1e40af' : '#6b7280',
                  borderBottom: activeTab === tab.key ? '2px solid #1e40af' : '2px solid transparent',
                  marginBottom: '-2px',
                  transition: 'all 0.15s',
                }}
              >
                {tab.icon} {tab.label}
                <span style={{
                  marginLeft: 8,
                  background: activeTab === tab.key ? '#dbeafe' : '#f3f4f6',
                  color: activeTab === tab.key ? '#1e40af' : '#6b7280',
                  borderRadius: 12, padding: '1px 7px', fontSize: 12, fontWeight: 700,
                }}>
                  {countFor(tab.key)}
                </span>
              </button>
            ))}
          </div>

          {/* ── Active tab card ── */}
          {TABS.map((tab) => tab.key === activeTab && (
            <div key={tab.key} className="card">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                <div>
                  <h3 style={{ margin: 0 }}>{tab.icon} {tab.label}</h3>
                  <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 13 }}>{tab.description}</p>
                </div>
                {activeData.length > 0 && (
                  <button
                    className="btn btn-secondary"
                    style={{ marginLeft: 'auto' }}
                    onClick={exportToExcel}
                  >
                    📥 Export to Excel
                  </button>
                )}
              </div>
              {renderTable(activeData)}
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default ClientInsights;
